import { merge, mergeWith, isObject, get } from "lodash";
import {
  getOrgChartPropertiesFromLocalStorage,
  getOrgChartDiffDatesFromLocalStorage,
} from "../utils/storeInLocalStorage";
import { ACTION_TYPES } from "./actionTypes";

export const initialOrgChartState = {
  // Teams/Members to display
  teams: {},
  members: {},

  viewOptions: {
    groupMembersBySupplyOrDemand: false,
    showPeople: true,
    showPrinciples: true,
    fromDate: null, // diff baseline date
    toDate: null, // diff next date
    showChanges: false,
    showChangelog: false, // toggle for changelog view
  },
  properties: {
    peopleData: [],
    teamsData: [],
  },
  expandedNodes: [],
  expandedPanel: {
    data: null,
  },
};

/**
 * Initializes org chart state with persisted properties and diff dates from localStorage.
 * Loads workspace-specific diff dates if workspaceId is provided.
 * @param {Object} params - Parameters object.
 * @param {Object} params.tagConfig - Tag configuration for filtering properties.
 * @param {string} params.teamId - The initial team ID to display.
 * @param {string|null|undefined} params.workspaceId - The workspace ID for loading workspace-specific dates.
 * @returns {Object} Initial org chart state with persisted data merged in.
 */
export const getInitialOrgChartState = ({ tagConfig, teamId, workspaceId }) => {
  const storedOrgChartProperties = getOrgChartPropertiesFromLocalStorage({
    tagConfig,
  });

  const storedDiffDates = getOrgChartDiffDatesFromLocalStorage(workspaceId);

  const defaultPanelEntityState = {
    expandedPanel: {
      data: {
        selectedEntity: teamId,
      },
    },
  };

  const stateWithStoredDates = {
    viewOptions: {
      ...initialOrgChartState.viewOptions,
      fromDate: storedDiffDates.fromDate,
      toDate: storedDiffDates.toDate,
      // Only enable showChanges if both dates are valid
      showChanges:
        storedDiffDates.fromDate?.isValid() &&
        storedDiffDates.toDate?.isValid(),
    },
  };

  return merge(
    {},
    initialOrgChartState,
    storedOrgChartProperties,
    stateWithStoredDates,
    defaultPanelEntityState
  );
};

// updates currentViewTeam if it was changed in the teams list
const updateViewTeam = (action, state, newState) => {
  // get changes to team matching view team
  const currentViewTeamChanges = get(
    action.payload?.teams,
    state.currentViewTeam?.id
  );

  // have changes to view team?
  if (currentViewTeamChanges) {
    return {
      ...newState,
      // mirror changes to view team
      currentViewTeam: {
        ...state.currentViewTeam,
        ...currentViewTeamChanges,
      },
    };
  }

  return newState;
};

export const orgChartReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.UPDATE_VIEW_OPTIONS: {
      return {
        ...state,
        viewOptions: {
          ...state.viewOptions,
          ...action.payload,
        },
      };
    }
    case ACTION_TYPES.UPDATE_PROPERTIES: {
      return {
        ...state,
        properties: {
          ...state.properties,
          ...action.payload,
        },
      };
    }
    case ACTION_TYPES.UPDATE_LOADED_TEAMS: {
      return updateViewTeam(action, state, {
        ...state,
        teams: { ...state.teams, ...action.payload },
      });
    }
    case ACTION_TYPES.UPDATE_PERSON_PARTIAL: {
      const { aggregateId } = action.payload;

      return {
        ...state,
        members: {
          ...state.members,
          [aggregateId]: {
            ...state.members[aggregateId],
            ...action.payload,
          },
        },
      };
    }
    case ACTION_TYPES.UPDATE_TEAM_TAGS: {
      const { teamId } = action.payload;

      return updateViewTeam(action, state, {
        ...state,
        teams: {
          ...state.teams,
          [teamId]: {
            ...state.teams[teamId],
            tags: action.payload.tags,
          },
        },
      });
    }
    case ACTION_TYPES.MERGE_PARTIAL_DATA: {
      const mergeHandler = (obj, source) => {
        if (isObject(obj) || isObject(source)) {
          return { ...obj, ...source };
        }

        return source || obj;
      };

      return updateViewTeam(action, state, {
        ...state,
        teams: mergeWith(state.teams, action.payload.teams, mergeHandler),
        members: mergeWith(state.members, action.payload.members, mergeHandler),
      });
    }
    case ACTION_TYPES.UPDATE_EXPANDED_PANEL: {
      return {
        ...state,
        expandedPanel: {
          ...state.expandedPanel,
          ...(action.payload.data !== undefined && {
            data: {
              ...state.expandedPanel.data,
              ...action.payload.data,
            },
          }),
        },
      };
    }
    case ACTION_TYPES.TOGGLE_CHANGELOG_VIEW: {
      return {
        ...state,
        viewOptions: {
          ...state.viewOptions,
          showChangelog: !state.viewOptions.showChangelog,
        },
      };
    }
    default: {
      return state;
    }
  }
};
